"use client";

import { motion, useScroll, useSpring } from "framer-motion";
import {
	Brain,
	Cpu,
	Download,
	FileText,
	Globe,
	Menu,
	Rocket,
	Shield,
	Terminal,
	X,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import MainLayout from "@/components/layout/main-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { SpotlightCard } from "@/components/ui/spotlight-card";
import { ArchitectureDiagram } from "@/components/whitepaper/architecture-diagram";
import { EconomicLoop } from "@/components/whitepaper/economic-loop";
import { useLanguage } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";

const sections = [
	{ id: "hero", title: "首页", enTitle: "Home" },
	{ id: "executive-summary", title: "执行摘要", enTitle: "Executive Summary" },
	{ id: "product-system", title: "产品体系", enTitle: "Product System" },
	{ id: "ai-narrative", title: "AI 叙事", enTitle: "AI Narrative" },
	{ id: "governance", title: "治理模式", enTitle: "Governance" },
	{ id: "economic-model", title: "经济模型", enTitle: "Economic Model" },
	{ id: "roadmap", title: "发展路线图", enTitle: "Roadmap" },
	{ id: "risks", title: "风险声明", enTitle: "Risks & Disclaimer" },
];

export default function WhitepaperPage() {
	const { language } = useLanguage();
	const [activeSection, setActiveSection] = useState("hero");
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const { scrollYProgress } = useScroll();
	const scaleX = useSpring(scrollYProgress, {
		stiffness: 100,
		damping: 30,
		restDelta: 0.001,
	});

	// Handle scroll spy
	useEffect(() => {
		const handleScroll = () => {
			const sectionElements = sections.map((s) =>
				document.getElementById(s.id),
			);
			const scrollPosition = window.scrollY + 200; // Offset

			for (let i = sectionElements.length - 1; i >= 0; i--) {
				const element = sectionElements[i];
				if (element && element.offsetTop <= scrollPosition) {
					setActiveSection(sections[i].id);
					break;
				}
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const scrollToSection = (id: string) => {
		const element = document.getElementById(id);
		if (element) {
			window.scrollTo({
				top: element.offsetTop - 100,
				behavior: "smooth",
			});
			setActiveSection(id);
			setIsMobileMenuOpen(false);
		}
	};

	const handleDownloadCN = () =>
		window.open("/pdf/Babyodin_Whitepaper_cn_v2.pdf", "_blank");
	const handleDownloadEN = () =>
		window.open("/pdf/Babyodin_Whitepaper_en_v2.pdf", "_blank");

	return (
		<MainLayout>
			{/* Adaptive Theme Wrapper */}
			<div className="min-h-screen bg-background text-foreground relative selection:bg-primary/30">
				{/* Global Dynamic Background */}
				<div className="fixed inset-0 z-0 pointer-events-none">
					{/* Scanline Overlay */}
					<div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.05)_50%),linear-gradient(90deg,rgba(255,0,0,0.02),rgba(0,255,0,0.01),rgba(0,0,255,0.02))] bg-[size:100%_4px,3px_100%] z-[100] pointer-events-none opacity-[0.03] dark:opacity-[0.05]" />

					{/* Light Mode Grid & Noise */}
					<div className="absolute inset-0 dark:hidden">
						<div
							className="absolute inset-0 opacity-[0.05]"
							style={{
								backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1'/%3E%3C/svg%3E")`,
							}}
						/>
						<div
							className="absolute inset-0"
							style={{
								backgroundImage: `
                  linear-gradient(to right, rgba(0, 0, 0, 0.1) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 1px, transparent 1px)
                `,
								backgroundSize: "40px 40px",
								maskImage:
									"radial-gradient(circle at 50% 0%, black 60%, transparent 100%)",
								WebkitMaskImage:
									"radial-gradient(circle at 50% 0%, black 60%, transparent 100%)",
							}}
						/>
					</div>

					{/* Dark Mode Grid & Noise */}
					<div className="absolute inset-0 hidden dark:block">
						<div
							className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
							style={{
								backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1'/%3E%3C/svg%3E")`,
							}}
						/>
						<div
							className="absolute inset-0"
							style={{
								backgroundImage: `
                  linear-gradient(to right, rgba(255, 255, 255, 0.08) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
                `,
								backgroundSize: "40px 40px",
								maskImage:
									"radial-gradient(circle at 50% 0%, black 40%, transparent 100%)",
								WebkitMaskImage:
									"radial-gradient(circle at 50% 0%, black 40%, transparent 100%)",
							}}
						/>
					</div>

					{/* Ambient Glows */}
					<div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 dark:bg-primary/20 blur-[150px] rounded-full mix-blend-multiply dark:mix-blend-screen animate-pulse-slow" />
					<div
						className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-accent/10 dark:bg-accent/20 blur-[150px] rounded-full mix-blend-multiply dark:mix-blend-screen animate-pulse-slow"
						style={{ animationDelay: "2s" }}
					/>
				</div>

				{/* Progress Bar */}
				<motion.div
					className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary z-[110] origin-left shadow-[0_0_15px_rgba(76,175,80,0.5)]"
					style={{ scaleX }}
				/>

				{/* Mobile Menu Button */}
				<div className="lg:hidden fixed bottom-24 lg:bottom-6 right-6 z-50">
					<Button
						onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						className="rounded-full w-14 h-14 shadow-xl bg-primary text-primary-foreground hover:bg-primary/90 border border-white/20 backdrop-blur-md"
					>
						{isMobileMenuOpen ? <X /> : <Menu />}
					</Button>
				</div>

				{/* Mobile Navigation Drawer */}
				{isMobileMenuOpen && (
					<motion.div
						initial={{ opacity: 0, y: 20, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						className="lg:hidden fixed bottom-40 right-6 bg-card/90 backdrop-blur-xl border border-primary/20 rounded-2xl shadow-2xl p-4 z-40 w-64 max-h-[60vh] overflow-y-auto"
					>
						<nav className="space-y-2">
							{sections.map((section) => (
								<button
									key={section.id}
									onClick={() => scrollToSection(section.id)}
									className={cn(
										"w-full text-left px-4 py-3 rounded-lg text-sm transition-all duration-300",
										activeSection === section.id
											? "bg-primary/10 text-primary font-bold shadow-[inset_0_0_10px_rgba(var(--primary),0.1)] border border-primary/30"
											: "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
									)}
								>
									{language === "zh" ? section.title : section.enTitle}
								</button>
							))}
						</nav>
					</motion.div>
				)}

				<div className="container mx-auto px-4 py-8 lg:py-12 flex gap-12 relative z-10 items-start">
					{/* Desktop Sidebar Navigation */}
					<aside className="hidden lg:block w-64 shrink-0 h-[calc(100vh-6rem)] sticky top-24 self-start">
						<SpotlightCard className="h-full flex flex-col !bg-white/70 dark:!bg-black/40 border-slate-200 dark:border-white/10 shadow-xl dark:shadow-2xl backdrop-blur-xl group/aside relative">
							{/* Corner Decorations */}
							<div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/30 rounded-tl-xl" />
							<div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary/30 rounded-br-xl" />

							<div className="p-6">
								<div className="flex items-center gap-2 mb-6 text-primary">
									<Terminal className="w-5 h-5" />
									<span className="font-mono text-sm font-bold tracking-wider">
										INDEX_V2.0
									</span>
								</div>
								<nav className="space-y-1 relative">
									<div className="absolute left-[11px] top-2 bottom-2 w-px bg-slate-200 dark:bg-white/10" />
									{sections.map((section) => (
										<button
											key={section.id}
											onClick={() => scrollToSection(section.id)}
											className={cn(
												"relative w-full text-left pl-8 py-2 text-sm transition-all duration-300 block group/btn",
												activeSection === section.id
													? "text-primary font-bold"
													: "text-slate-500 dark:text-gray-400 hover:text-foreground",
											)}
										>
											<span
												className={cn(
													"absolute left-[7px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 transition-all duration-300 bg-background z-10",
													activeSection === section.id
														? "border-primary bg-primary shadow-[0_0_8px_rgba(76,175,80,0.6)] scale-110"
														: "border-slate-300 dark:border-gray-600 group-hover/btn:border-primary/50",
												)}
											/>
											{language === "zh" ? section.title : section.enTitle}
										</button>
									))}
								</nav>
							</div>

							<div className="mt-auto p-6 border-t border-slate-100 dark:border-white/10 bg-slate-50/50 dark:bg-white/5">
								<p className="text-[10px] text-slate-400 dark:text-gray-500 mb-3 font-mono uppercase tracking-wider">
									{language === "zh" ? "加密传输" : "Encrypted Download"}
								</p>
								<div className="space-y-2">
									<Button
										onClick={handleDownloadCN}
										variant="outline"
										size="sm"
										className="w-full justify-start gap-2 h-9 bg-white dark:bg-black/50 border-slate-200 dark:border-white/10 hover:bg-primary/5 dark:hover:bg-primary/20 hover:border-primary/50 hover:text-primary transition-all group text-slate-600 dark:text-gray-300"
									>
										<Download className="w-3.5 h-3.5 group-hover:animate-bounce" />
										<span className="text-xs">中文版 V2.0</span>
									</Button>
									<Button
										onClick={handleDownloadEN}
										variant="outline"
										size="sm"
										className="w-full justify-start gap-2 h-9 bg-white dark:bg-black/50 border-slate-200 dark:border-white/10 hover:bg-primary/5 dark:hover:bg-primary/20 hover:border-primary/50 hover:text-primary transition-all group text-slate-600 dark:text-gray-300"
									>
										<Download className="w-3.5 h-3.5 group-hover:animate-bounce" />
										<span className="text-xs">English V2.0</span>
									</Button>
								</div>
							</div>
						</SpotlightCard>
					</aside>

					{/* Main Content */}
					<main className="flex-1 min-w-0 space-y-24 pb-20">
						{/* Hero Section */}
						<section
							id="hero"
							className="min-h-[85vh] flex flex-col justify-center relative rounded-[3rem] overflow-hidden border border-slate-200 dark:border-white/10 shadow-2xl dark:shadow-[0_0_50px_rgba(0,0,0,0.5)] bg-slate-50/30 dark:bg-transparent backdrop-blur-sm group/hero"
						>
							{/* Internal Corner Decorations */}
							<div className="absolute top-8 left-8 w-8 h-8 border-t border-l border-primary/20" />
							<div className="absolute bottom-8 right-8 w-8 h-8 border-b border-r border-primary/20" />

							<div className="relative z-10 p-8 md:p-16 text-center md:text-left flex flex-col justify-center h-full">
								<motion.div
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.8, ease: "easeOut" }}
								>
									<div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 dark:bg-primary/10 text-primary text-xs font-mono mb-8 animate-pulse-slow shadow-sm">
										<span className="relative flex h-2 w-2">
											<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
											<span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
										</span>
										SYSTEM_READY // BABYODIN_DOCS_V2
									</div>

									<h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-6 text-slate-900 dark:text-white leading-[1.1] relative">
										<span className="relative z-10 inline-block bg-[linear-gradient(110deg,#0f172a,45%,#4ade80,55%,#0f172a)] dark:bg-[linear-gradient(110deg,#ffffff,45%,#4ade80,55%,#ffffff)] bg-[length:250%_100%] bg-clip-text text-transparent animate-text-shimmer drop-shadow-sm dark:drop-shadow-[0_0_30px_rgba(76,175,80,0.3)]">
											BabyOdin
										</span>
									</h1>

									<p className="text-2xl md:text-4xl font-bold mb-6 text-slate-800 dark:text-white/90 drop-shadow-md">
										{language === "zh"
											? "BTC 链上操作系统"
											: "The OS for BTC On-Chain World"}
									</p>

									<p className="text-lg md:text-xl text-slate-600 dark:text-gray-400 max-w-2xl mb-12 leading-relaxed font-light">
										{language === "zh"
											? "不仅仅是工具。我们在构建一个自我演化、永续通缩的链上生态系统。从数据洞察到智能协作，重新定义你的链上生存方式。"
											: "More than just tools. We are building a self-evolving, perpetually deflationary on-chain ecosystem. From data insights to intelligent collaboration, redefining your on-chain survival."}
									</p>

									<div className="flex flex-col sm:flex-row gap-6">
										<Button
											onClick={handleDownloadCN}
											size="lg"
											className="group relative overflow-hidden rounded-full px-8 py-7 text-base font-bold bg-primary text-white hover:bg-primary/90 dark:bg-white dark:text-black dark:hover:bg-gray-200 transition-all hover:scale-105 shadow-xl shadow-primary/20 dark:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
										>
											<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-black/10 to-transparent -translate-x-full group-hover:animate-shimmer" />
											<span className="flex items-center gap-2">
												<FileText className="w-5 h-5" />
												{language === "zh"
													? "下载中文白皮书"
													: "Download CN PDF"}
											</span>
										</Button>
										<Button
											onClick={handleDownloadEN}
											variant="outline"
											size="lg"
											className="rounded-full px-8 py-7 text-base font-bold border-slate-300 dark:border-white/30 text-slate-700 dark:text-white bg-white/50 dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 hover:border-slate-400 dark:hover:border-white/60 hover:scale-105 transition-all backdrop-blur-md shadow-sm"
										>
											<span className="flex items-center gap-2">
												<Download className="w-5 h-5" />
												{language === "zh"
													? "下载英文白皮书"
													: "Download EN PDF"}
											</span>
										</Button>
									</div>
								</motion.div>
							</div>
						</section>

						{/* Executive Summary */}
						<section id="executive-summary" className="scroll-mt-32">
							<div className="mb-8 flex items-center gap-3 text-slate-900 dark:text-white">
								<div className="font-mono text-sm opacity-50">[00]</div>
								<h2 className="text-3xl font-bold tracking-tight uppercase">
									{language === "zh" ? "执行摘要" : "Executive Summary"}
								</h2>
								<div className="flex-1 h-px bg-gradient-to-r from-primary/30 to-transparent ml-4" />
							</div>
							<SpotlightCard className="p-8 md:p-12">
								<div className="flex flex-col md:flex-row gap-12 items-start">
									<div className="flex-1 space-y-6">
										<p className="text-lg leading-relaxed text-slate-600 dark:text-gray-400">
											{language === "zh"
												? "比特币正在经历从“数字黄金”到“链上经济体”的范式转移。在这个全新的阶段，普通用户面临着信息过载、工具割裂和认知壁垒的三重挑战。"
												: "Bitcoin is undergoing a paradigm shift from 'Digital Gold' to an 'On-Chain Economy'. In this new phase, users face the triple challenges of information overload, fragmented tools, and cognitive barriers."}
										</p>

										<div className="pl-4 border-l-2 border-primary/50 py-2 space-y-2">
											<p className="text-xl font-medium text-slate-900 dark:text-white">
												{language === "zh"
													? "BabyOdin 不止是工具箱。"
													: "BabyOdin is not just a toolbox."}
											</p>
											<p className="text-slate-600 dark:text-gray-400 text-base">
												{language === "zh"
													? "它是用户进入 BTC 链上世界的第一站。通过 AI 驱动的数据层和自动化的执行层，我们将复杂的链上博弈转化为直观的交互体验。"
													: "It is the first stop for users entering the BTC on-chain world. Through an AI-driven data layer and automated execution layer, we transform complex on-chain games into intuitive interactive experiences."}
											</p>
										</div>
									</div>

									{/* Decorative Tech Element */}
									<div className="w-full md:w-1/3 aspect-square bg-slate-100 dark:bg-gradient-to-br dark:from-primary/10 dark:to-accent/10 rounded-2xl border border-slate-200 dark:border-white/5 relative overflow-hidden flex items-center justify-center">
										<div className="absolute inset-0 bg-noise opacity-10" />
										<div className="text-center space-y-2 relative z-10">
											<div className="text-5xl font-black text-primary tracking-tighter drop-shadow-sm">
												133
											</div>
											<div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-gray-500 font-bold">
												Successful Runes
											</div>
											<div className="w-12 h-1 bg-primary/50 mx-auto mt-4 rounded-full" />
											<div className="text-[10px] text-slate-400 dark:text-gray-500 mt-2 font-mono">
												/ 24,000+ ISSUED
											</div>
										</div>
									</div>
								</div>
							</SpotlightCard>
						</section>

						{/* Product System */}
						<section id="product-system" className="scroll-mt-32">
							<div className="mb-10">
								<div className="flex items-center gap-3 text-slate-900 dark:text-white mb-2">
									<div className="font-mono text-sm opacity-50">[01]</div>
									<h2 className="text-3xl font-bold tracking-tight uppercase">
										{language === "zh" ? "产品体系" : "Product System"}
									</h2>
									<div className="flex-1 h-px bg-gradient-to-r from-primary/30 to-transparent ml-4" />
								</div>
								<p className="text-lg text-slate-600 dark:text-gray-400 max-w-2xl font-light">
									{language === "zh"
										? "基于能力分层的模块化架构，构建 BTC 链上操作系统的核心基石。"
										: "Modular architecture based on capability layering, building the core foundation of the BTC OS."}
								</p>
							</div>
							<ArchitectureDiagram language={language} />
						</section>

						{/* AI Narrative */}
						<section id="ai-narrative" className="scroll-mt-32">
							<div className="mb-8 flex items-center gap-3 text-slate-900 dark:text-white">
								<div className="font-mono text-sm opacity-50">[02]</div>
								<h2 className="text-3xl font-bold tracking-tight uppercase">
									{language === "zh" ? "AI 叙事" : "AI Narrative"}
								</h2>
								<div className="flex-1 h-px bg-gradient-to-r from-primary/30 to-transparent ml-4" />
							</div>
							<SpotlightCard className="p-8 md:p-12 overflow-visible relative">
								{/* Visual Accent */}
								<div className="absolute top-0 right-0 w-24 h-24 bg-accent/5 blur-3xl rounded-full" />

								<div className="grid md:grid-cols-2 gap-16">
									<div className="space-y-8">
										<div className="flex items-center gap-3 text-primary dark:text-accent">
											<Brain className="w-8 h-8" />
											<h3 className="text-2xl font-bold text-slate-900 dark:text-white">
												{language === "zh"
													? "AI 智能协作层"
													: "AI Collaboration Layer"}
											</h3>
										</div>

										<p className="text-lg text-slate-600 dark:text-gray-400 font-light">
											{language === "zh"
												? "面对 BTC 生态指数级增长的信息熵，AI 是实现“降维打击”的唯一路径。我们让 AI 成为你的链上副驾驶。"
												: "Facing exponential info entropy in BTC, AI is the only path to clarity. We make AI your on-chain co-pilot."}
										</p>

										<div className="space-y-4">
											{[
												{
													title: "信息压缩",
													desc: "Information Compression",
													icon: "01",
												},
												{
													title: "认知辅助",
													desc: "Cognitive Assistance",
													icon: "02",
												},
												{
													title: "持续陪伴",
													desc: "Continuous Support",
													icon: "03",
												},
											].map((item) => (
												<div
													key={item.icon}
													className="group flex items-center gap-4 p-4 rounded-xl bg-slate-100/50 dark:bg-secondary/20 border border-transparent hover:border-primary/30 transition-all hover:bg-slate-200/50 dark:hover:bg-secondary/30"
												>
													<div className="font-mono text-xl font-bold text-primary/40 group-hover:text-primary transition-colors">
														[{item.icon}]
													</div>
													<div>
														<div className="font-bold text-slate-900 dark:text-white text-sm">
															{item.title}
														</div>
														<div className="text-[10px] text-slate-500 dark:text-gray-500 font-mono uppercase tracking-tighter">
															{item.desc}
														</div>
													</div>
												</div>
											))}
										</div>
									</div>

									{/* AI Chat Interface */}
									<div className="relative">
										<div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-accent/30 opacity-20 blur-xl rounded-3xl" />
										<div className="relative bg-white dark:bg-[#0F1115] border border-slate-200 dark:border-white/10 rounded-2xl p-6 shadow-2xl font-mono text-xs h-full flex flex-col min-h-[300px]">
											<div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-white/5 pb-4">
												<div className="flex gap-1.5">
													<div className="w-2.5 h-2.5 rounded-full bg-red-500/40" />
													<div className="w-2.5 h-2.5 rounded-full bg-yellow-500/40" />
													<div className="w-2.5 h-2.5 rounded-full bg-green-500/40" />
												</div>
												<div className="text-[10px] text-slate-400 dark:text-gray-500 font-bold tracking-tighter">
													CORE_INTERFACE_V2.0
												</div>
											</div>

											<div className="space-y-6 flex-1 overflow-hidden">
												<div className="flex gap-3">
													<div className="w-6 h-6 rounded bg-slate-100 dark:bg-secondary/50 flex items-center justify-center text-[10px] text-slate-500 dark:text-white font-bold shrink-0">
														U
													</div>
													<div className="bg-slate-50 dark:bg-secondary/10 p-3 rounded-lg rounded-tl-none border border-slate-100 dark:border-white/5 w-full shadow-sm">
														<p className="text-slate-700 dark:text-gray-300 leading-normal">
															{language === "zh"
																? "分析 $DOG 过去 24h 资金波动。"
																: "Analyze $DOG fund volatility 24h."}
														</p>
													</div>
												</div>

												<div className="flex gap-3 flex-row-reverse">
													<div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center text-primary text-[10px] shrink-0 font-black">
														AI
													</div>
													<div className="bg-primary/5 p-3 rounded-lg rounded-tr-none border border-primary/20 w-full relative overflow-hidden shadow-sm">
														<div className="absolute top-0 left-0 w-0.5 h-full bg-primary/50" />
														<div className="space-y-2">
															<div className="flex items-center gap-2 text-primary text-[10px] mb-1 font-bold">
																<span className="relative flex h-1.5 w-1.5">
																	<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
																	<span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary"></span>
																</span>
																SCAN_COMPLETE
															</div>
															<p className="text-slate-700 dark:text-gray-300 leading-relaxed text-[11px]">
																{language === "zh"
																	? "大户净流入 +12.5 BTC。检测到 3 个新鲸鱼地址活跃。关键支撑位：$0.0045。"
																	: "Whale net +12.5 BTC. 3 new whale wallets active. Support: $0.0045."}
															</p>
															<div className="grid grid-cols-2 gap-2 mt-2">
																<div className="bg-white dark:bg-black/30 p-1.5 rounded-[4px] text-[9px] text-center border border-slate-200 dark:border-white/5 cursor-pointer hover:border-primary/50 transition-colors text-slate-600 dark:text-white font-bold">
																	VIEW_CHART
																</div>
																<div className="bg-white dark:bg-black/30 p-1.5 rounded-[4px] text-[9px] text-center border border-slate-200 dark:border-white/5 cursor-pointer hover:border-primary/50 transition-colors text-slate-600 dark:text-white font-bold">
																	SET_ALERT
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</SpotlightCard>
						</section>

						{/* Governance */}
						<section id="governance" className="scroll-mt-32">
							<div className="mb-10 flex items-center gap-3 text-slate-900 dark:text-white">
								<div className="font-mono text-sm opacity-50">[03]</div>
								<h2 className="text-3xl font-bold tracking-tight uppercase">
									{language === "zh" ? "治理模式" : "Governance"}
								</h2>
								<div className="flex-1 h-px bg-gradient-to-r from-primary/30 to-transparent ml-4" />
							</div>

							<div className="grid md:grid-cols-3 gap-6">
								<SpotlightCard className="md:col-span-2 p-8">
									<div className="flex items-center gap-4 mb-6">
										<div className="p-3 rounded-2xl bg-primary/10 border border-primary/20">
											<Shield className="w-8 h-8 text-primary" />
										</div>
										<h3 className="text-2xl font-bold text-slate-900 dark:text-white">
											{language === "zh"
												? "CTO 社区治理DAO"
												: "CTO Community DAO"}
										</h3>
									</div>
									<p className="text-lg text-slate-600 dark:text-gray-400 leading-relaxed mb-8 font-light">
										{language === "zh"
											? "我们不追求形式上的绝对去中心化，而是追求“长期主义的共识”。基金会作为执行层，DAO 作为决策层，共同构建一个抗脆弱的生态系统。"
											: "We don't chase formal decentralization, but 'long-term consensus'. Foundation executes, DAO decides, building an anti-fragile ecosystem together."}
									</p>
									<div className="flex gap-3">
										<Badge
											variant="outline"
											className="px-4 py-1.5 bg-primary/5 border-primary/20 text-primary font-mono text-[10px] tracking-widest uppercase"
										>
											DAO_DRIVEN
										</Badge>
										<Badge
											variant="outline"
											className="px-4 py-1.5 bg-primary/5 border-primary/20 text-primary font-mono text-[10px] tracking-widest uppercase"
										>
											TRANSPARENT
										</Badge>
									</div>
								</SpotlightCard>

								<SpotlightCard className="p-8 flex flex-col justify-center items-center text-center bg-slate-50 dark:bg-secondary/5 relative overflow-hidden">
									<div className="absolute top-0 right-0 w-12 h-12 border-t border-r border-primary/10" />
									<div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(76,175,80,0.3)]">
										<Globe className="w-8 h-8 text-white" />
									</div>
									<h4 className="font-bold mb-2 text-lg text-slate-900 dark:text-white tracking-tight">
										Global Node
									</h4>
									<p className="text-xs text-slate-500 dark:text-gray-500 font-mono leading-relaxed">
										DISTRIBUTED GOVERNANCE
										<br />
										INFRASTRUCTURE
									</p>
								</SpotlightCard>
							</div>
						</section>

						{/* Economic Model */}
						<section id="economic-model" className="scroll-mt-32">
							<div className="mb-16 text-center">
								<div className="inline-block px-4 py-1.5 rounded-full bg-primary/5 border border-primary/20 text-primary font-mono text-[10px] tracking-[0.3em] uppercase mb-4">
									Tokenomics_Engine
								</div>
								<h2 className="text-4xl md:text-5xl font-black mb-6 text-slate-900 dark:text-white tracking-tighter">
									{language === "zh"
										? "经济模型：通缩引擎"
										: "Deflationary Engine"}
								</h2>
								<div className="flex justify-center items-center gap-4 text-slate-500 dark:text-gray-400 font-light text-lg">
									<span>21M Supply</span>
									<div className="w-1.5 h-1.5 rounded-full bg-primary/30" />
									<span>Real Yield</span>
									<div className="w-1.5 h-1.5 rounded-full bg-primary/30" />
									<span>Auto Burn</span>
								</div>
							</div>
							<EconomicLoop language={language} />
						</section>

						{/* Roadmap */}
						<section id="roadmap" className="scroll-mt-32">
							<div className="mb-12 flex items-center gap-3 text-slate-900 dark:text-white">
								<div className="font-mono text-sm opacity-50">[04]</div>
								<h2 className="text-3xl font-bold tracking-tight uppercase">
									{language === "zh" ? "发展路线图" : "Roadmap"}
								</h2>
								<div className="flex-1 h-px bg-gradient-to-r from-primary/30 to-transparent ml-4" />
							</div>

							<div className="space-y-6 relative">
								{/* Connecting Line */}
								<div className="absolute left-[27px] top-8 bottom-8 w-px bg-slate-200 dark:bg-white/10 z-0" />

								{[
									{
										phase: "01",
										title:
											language === "zh"
												? "AI 解读与提示"
												: "AI Interpretation & Prompts",
										desc:
											language === "zh"
												? "建立基础数据层，提供实时的链上风险预警与机会提示。"
												: "Establish basic data layer, providing real-time on-chain risk alerts and opportunity prompts.",
										status: "current",
									},
									{
										phase: "02",
										title:
											language === "zh"
												? "AI 个性化与策略"
												: "AI Personalization & Strategy",
										desc:
											language === "zh"
												? "根据用户画像生成定制化策略，提供复盘与优化建议。"
												: "Generate customized strategies based on user profiles, providing review and optimization suggestions.",
										status: "upcoming",
									},
									{
										phase: "03",
										title:
											language === "zh"
												? "AI + DAO 协作"
												: "AI + DAO Collaboration",
										desc:
											language === "zh"
												? "AI 辅助治理决策，成为连接社区、资金与项目的智能中枢。"
												: "AI assists governance decisions, becoming the intelligent hub connecting community, capital, and projects.",
										status: "future",
									},
								].map((step, idx) => (
									<div key={idx} className="relative z-10 pl-20 group/step">
										<div
											className={cn(
												"absolute left-0 top-0 w-14 h-14 rounded-2xl border flex items-center justify-center font-mono text-xl font-bold transition-all duration-500 shadow-sm",
												step.status === "current"
													? "bg-slate-900 dark:bg-black border-primary text-primary shadow-[0_0_20px_rgba(76,175,80,0.3)]"
													: "bg-slate-100 dark:bg-black border-slate-200 dark:border-border text-slate-400 dark:text-gray-500 group-hover/step:border-primary/50 group-hover/step:text-primary dark:group-hover/step:text-white",
											)}
										>
											{step.phase}
										</div>

										<SpotlightCard className="p-6 md:p-8 group-hover/step:border-primary/30 transition-colors">
											<div className="flex justify-between items-start mb-2">
												<h3 className="text-xl font-bold text-slate-900 dark:text-white group-hover/step:text-primary transition-colors">
													{step.title}
												</h3>
												{step.status === "current" && (
													<Badge className="bg-primary/10 text-primary border-primary/20 font-mono text-[9px] uppercase tracking-tighter">
														In_Progress
													</Badge>
												)}
											</div>
											<p className="text-slate-600 dark:text-gray-400 font-light leading-relaxed">
												{step.desc}
											</p>
										</SpotlightCard>
									</div>
								))}
							</div>
						</section>

						{/* Risks & Disclaimer */}
						<section id="risks" className="scroll-mt-32 mb-20">
							<div className="bg-slate-100/50 dark:bg-[#0F1115] rounded-[2rem] p-8 md:p-12 border border-slate-200 dark:border-white/5 relative overflow-hidden">
								<div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[100px]" />

								<div className="flex items-center gap-3 mb-8 text-slate-900 dark:text-white">
									<Shield className="w-6 h-6 text-primary" />
									<h3 className="text-xl font-extrabold uppercase tracking-tight">
										{language === "zh"
											? "风险声明与免责条款"
											: "Risks & Disclaimer"}
									</h3>
								</div>

								<div className="text-xs md:text-sm text-slate-600 dark:text-gray-400 space-y-6 max-h-60 overflow-y-auto pr-4 custom-scrollbar leading-relaxed font-light">
									<p>
										{language === "zh"
											? "BabyOdin 致力于为 BTC 链上生态提供长期可用的产品与基础设施，但任何去中心化技术、金融系统或开放生态，都不可避免地伴随风险。"
											: "BabyOdin is committed to providing long-term viable products and infrastructure for the BTC chain ecosystem. However, any decentralized technology, financial system, or open ecosystem inevitably carries inherent risks."}
									</p>
									<p>
										{language === "zh"
											? "本白皮书及 BabyOdin 提供的所有内容，仅用于信息展示与产品说明目的，不构成任何形式的投资建议、法律意见或财务建议。"
											: "This whitepaper and all content provided by BabyOdin are intended solely for informational and product description purposes and do not constitute any form of investment advice, legal advice, or financial or tax advice."}
									</p>
									<p>
										{language === "zh"
											? "BabyOdin 不托管用户数字资产，不持有用户私钥。用户应充分理解相关风险，在必要时咨询专业人士。"
											: "BabyOdin does not custody user digital assets and does not hold user private keys. Users should fully understand the associated risks and consult professionals when necessary."}
									</p>
								</div>
							</div>
						</section>
					</main>
				</div>
			</div>
		</MainLayout>
	);
}
